import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import Cropper from 'react-easy-crop';
import { db } from '../config/firebase';
import { getCurrentUser } from '../services/authService';

async function getCroppedImg(imageSrc, croppedAreaPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    croppedAreaPixels.x, croppedAreaPixels.y,
    croppedAreaPixels.width, croppedAreaPixels.height,
    0, 0, croppedAreaPixels.width, croppedAreaPixels.height
  );
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
}

function ProfilePage() {

  const user = getCurrentUser();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');

  const photoInputRef = useRef(null);
  const [photoURL, setPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoSuccess, setPhotoSuccess] = useState('');

  const [photoCropSrc, setPhotoCropSrc] = useState('');
  const [photoCrop, setPhotoCrop] = useState({ x: 0, y: 0 });
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoCroppedAreaPixels, setPhotoCroppedAreaPixels] = useState(null);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setProfile(data);
        setFirstName(data.firstName || data.profile?.firstName || '');
        setLastName(data.lastName || data.profile?.lastName || '');
        setCompany(data.profile?.company || '');
        setPhotoURL(data.photoURL || '');
      }
      setLoading(false);
    });
  }, [user]);

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const displayName = `${firstName.trim()} ${lastName.trim()}`;
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName,
      });
      setProfile(prev => ({ ...prev, firstName: firstName.trim(), lastName: lastName.trim(), displayName }));
      setSuccess('Profile updated.');
      setEditing(false);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFirstName(profile?.firstName || profile?.profile?.firstName || '');
    setLastName(profile?.lastName || profile?.profile?.lastName || '');
    setCompany(profile?.profile?.company || '');
    setError('');
    setEditing(false);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoError('');
    setPhotoSuccess('');
    setPhotoFile(null);
    setPhotoPreview('');

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setPhotoError('Only JPG and PNG files are supported.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setPhotoError('Image must be 2MB or smaller.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoCropSrc(reader.result);
      setPhotoCrop({ x: 0, y: 0 });
      setPhotoZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = async () => {
    if (!photoFile || !user) return;
    setPhotoUploading(true);
    setPhotoError('');
    setPhotoSuccess('');
    try {
      const storage = getStorage();
      const fileRef = storageRef(storage, `users/${user.uid}/profile-photo.jpg`);
      await uploadBytes(fileRef, photoFile);
      const downloadURL = await getDownloadURL(fileRef);
      await updateDoc(doc(db, 'users', user.uid), { photoURL: downloadURL });
      setPhotoURL(downloadURL);
      setPhotoFile(null);
      setPhotoPreview('');
      setPhotoSuccess('Photo saved successfully.');
      if (photoInputRef.current) photoInputRef.current.value = '';
    } catch {
      setPhotoError('Upload failed. Please try again.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePhotoCropConfirm = async () => {
    try {
      const blob = await getCroppedImg(photoCropSrc, photoCroppedAreaPixels);
      setPhotoPreview(URL.createObjectURL(blob));
      setPhotoFile(blob);
      setPhotoCropSrc('');
    } catch {
      setPhotoError('Could not process image. Please try another file.');
      setPhotoCropSrc('');
    }
  };

  const fieldStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '15px',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.4rem',
    fontWeight: '500',
    fontSize: '14px',
    color: '#374151',
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6' }}>
        <div style={{ color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  return (
    <>
      {/* Content */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ backgroundColor: '#F8FAFC', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '2rem' }}>

          {error && (
            <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '14px' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '14px' }}>
              {success}
            </div>
          )}

          {/* Profile Picture */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Profile Picture</label>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <div style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: '#f9fafb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {(photoPreview || photoURL) ? (
                  <img
                    src={photoPreview || photoURL}
                    alt="Profile"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handlePhotoSelect}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => photoInputRef.current?.click()}
                  style={{ backgroundColor: 'white', color: '#374151', padding: '0.45rem 0.85rem', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}
                >
                  Choose Image
                </button>
                <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: '1.4' }}>
                  Square image only · JPG or PNG · Max 2MB
                </div>
                {photoError && (
                  <div style={{ color: '#991b1b', fontSize: '13px', marginTop: '0.35rem' }}>{photoError}</div>
                )}
                {photoSuccess && (
                  <div style={{ color: '#065f46', fontSize: '13px', marginTop: '0.35rem' }}>{photoSuccess}</div>
                )}
                {photoFile && (
                  <button
                    onClick={handlePhotoUpload}
                    disabled={photoUploading}
                    style={{ marginTop: '0.5rem', backgroundColor: '#E8590C', color: 'white', padding: '0.45rem 0.85rem', borderRadius: '6px', border: 'none', cursor: photoUploading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500', opacity: photoUploading ? 0.6 : 1 }}
                  >
                    {photoUploading ? 'Uploading...' : 'Save Photo'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Email — always read-only */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Email</label>
            <div style={{ padding: '0.5rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '15px', color: '#6b7280' }}>
              {user?.email}
            </div>
          </div>

          {editing ? (
            <>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First Name</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={fieldStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last Name</label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={fieldStyle} />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Company Name</label>
                <div style={{ padding: '0.5rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '15px', color: '#374151' }}>
                  {company || '—'}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '0.35rem' }}>
                  Edit your company name in{' '}
                  <span
                    style={{ color: '#E8590C', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate('/account/profile')}
                  >
                    Company Profile
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{ flex: 1, backgroundColor: '#E8590C', color: 'white', padding: '0.65rem', borderRadius: '6px', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: saving ? 0.6 : 1 }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  style={{ flex: 1, backgroundColor: 'white', color: '#374151', padding: '0.65rem', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', fontWeight: '500' }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First Name</label>
                  <div style={{ padding: '0.5rem', fontSize: '15px' }}>{profile?.firstName || profile?.profile?.firstName || '—'}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last Name</label>
                  <div style={{ padding: '0.5rem', fontSize: '15px' }}>{profile?.lastName || profile?.profile?.lastName || '—'}</div>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Company Name</label>
                <div style={{ padding: '0.5rem', fontSize: '15px' }}>{profile?.profile?.company || '—'}</div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '0.35rem' }}>
                  Edit your company name in{' '}
                  <span
                    style={{ color: '#E8590C', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate('/account/profile')}
                  >
                    Company Profile
                  </span>
                </div>
              </div>

              <button
                onClick={() => { setSuccess(''); setEditing(true); }}
                style={{ padding: '8px 20px', backgroundColor: '#E8590C', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'block', marginLeft: 'auto' }}
              >
                Edit Profile
              </button>
            </>
          )}
        </div>
      </div>

      {/* Photo Crop Modal */}
      {photoCropSrc && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '0', width: '340px', overflow: 'hidden' }}>
            <div style={{ position: 'relative', width: '340px', height: '340px', background: '#000' }}>
              <Cropper
                image={photoCropSrc}
                crop={photoCrop}
                zoom={photoZoom}
                aspect={1}
                onCropChange={setPhotoCrop}
                onZoomChange={setPhotoZoom}
                onCropComplete={(_, croppedAreaPixels) => setPhotoCroppedAreaPixels(croppedAreaPixels)}
              />
            </div>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input type="range" min={1} max={3} step={0.01} value={photoZoom} onChange={e => setPhotoZoom(Number(e.target.value))} style={{ width: '100%' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setPhotoCropSrc('')} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={handlePhotoCropConfirm} style={{ flex: 1, padding: '10px', background: '#E8590C', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>Crop & Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default ProfilePage;
